import React, { useState, useEffect } from "react";
import Header from "../../../Components/Header";
import Seo from "../../../Components/SeoComponent/Seo";
import { showSuccessToast, showErrorToast } from "../../../utils/toastUtils";
import { fetchProfile } from "../../../Services/ApiTestService/aadharTestService";
import { verifyAadharPanLink } from "../../../Services/ApiTestService/apiTestService";
import { InputWithLabel, Button } from "../../../Components/Tags/Tags";
import Loader from "../../../Loader";
import { Data7 } from "../Data";
import { Link } from "react-router-dom";
import { IoArrowBackCircleOutline } from "react-icons/io5";
import { useService } from "../../../context/ServiceContext";

const AadharPankLink = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [listData, setListData] = useState({});
  const [errors, setErrors] = useState({});
  const { serviceName } = useService();
  const [profileData, setProfileData] = useState({});
  const [aadharNumber, setAadharNumber] = useState("");
  const [dob, setDob] = useState("");

  // Fetch profile data on mount
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setIsLoading(true);
        const response = await fetchProfile();
        setProfileData(response.data);
      } catch (error) {
        console.error("Error fetching profile data:", error);
        showErrorToast("Failed to fetch profile data.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfileData();
  }, []);

  // Form validation
  // const validateForm = (aadharNumber) => {
  //   const errors = {};

  //   // DL number must be 2 letters + 13 digits = 15 characters
  //   const dlRegex = /^[A-Z]{2}[0-9]{13}$/;

  //   if (!aadharNumber.trim()) {
  //     errors.aadharNumber = "Driving License number is required.";
  //   } else if (!dlRegex.test(aadharNumber.toUpperCase())) {
  //     errors.aadharNumber = "DL number must be 2 letters followed by 13 digits (e.g., BR0420270023150).";
  //   }

  //   return errors;
  // };

  const validateForm = (aadharNumber) => {
    const errors = {};

    // Aadhaar: 12 digits only
    const aadhaarRegex = /^[0-9]{12}$/;

    // Remove spaces if user enters like "1234 5678 9012"
    const cleanAadhar = aadharNumber.replace(/\s+/g, "");

    if (!cleanAadhar) {
      errors.aadharNumber = "Aadhaar number is required.";
    } else if (!aadhaarRegex.test(cleanAadhar)) {
      errors.aadharNumber = "Aadhaar number must be exactly 12 digits.";
    }

    return errors;
  };

  const handleSubmit = async () => {
    const validationErrors = validateForm(aadharNumber);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    try {
      setIsLoading(true);

      const response = await verifyAadharPanLink({
        api_id: profileData?.user_id,
        api_key: profileData?.api_key,
        token_id: profileData?.token_id,
        aadhar_number: aadharNumber,
      });

      const statusCode = response?.status?.code || response?.status;
      if (statusCode === 200) {
        const dlData = response?.data;

        // ✅ Check if valid object or array
        if (dlData && (typeof dlData === "object" || Array.isArray(dlData))) {
          setListData(dlData);
          showSuccessToast(
            response.status?.message || "Aadhaar pan link check successful.",
          );

          // Clear form
          setAadharNumber("");
          setErrors({});
        } else {
          showErrorToast("aadhar No. missing or in an unexpected format.");
        }
      } else if (statusCode === 422) {
        const backendErrors = response.data.message || {};

        showErrorToast(response.data.message || "Invalid format or data.");
      } else if (statusCode === 500) {
        const errorMessage =
          response.data.message || response?.message || "Server error occurred";
        showErrorToast(errorMessage);
      } else {
        showErrorToast(
          response.status?.message ||
            `Unexpected error occurred (code: ${statusCode})`,
        );
      }
    } catch (error) {
      console.error("Aadhar Details Error:", error);
      showErrorToast("Failed to verify Aadhar  details. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Seo />
      {isLoading && <Loader />}
      <Header heading={serviceName} />

      <div className="max-w-4xl mx-auto p-6 space-y-6 flex flex-col justify-center items-center">
        {/* Top Navigation Row */}
        <div className="flex w-full justify-between items-center">
          <div>
            <h1 className="text-xl font-bold text-slate-800">
              Verification Portal
            </h1>
            <p className="text-sm text-slate-500 font-medium">
              Verify your linking status instantly
            </p>
          </div>
          <Link
            to="/TestMaster"
            className="flex items-center gap-2 text-slate-500 bg-white border border-slate-200 hover:text-slate-800 hover:border-slate-400 px-4 py-2 rounded-xl font-bold text-sm transition-all duration-200 shadow-sm"
          >
            <IoArrowBackCircleOutline size={22} />
            <span>Go Back</span>
          </Link>
        </div>

        {/* Main Form Card */}
        <div className="bg-white border w-fit border-slate-200 rounded-[2rem] p-8 shadow-2xl shadow-slate-200/60">
          <div className="flex flex-col md:flex-row items-end gap-6">
            {/* Standard Input Section */}
            <div className="flex-1 w-full space-y-2">
              <label
                htmlFor="aadhar_number"
                className="block text-[12px] font-black text-slate-400 uppercase tracking-widest ml-1"
              >
                Aadhaar Number
              </label>
              <input
                type="text"
                id="aadhar_number"
                name="aadhar_number"
                placeholder="Enter 12-digit Aadhaar"
                required
                value={aadharNumber}
                onChange={(e) => setAadharNumber(e.target.value)}
                className={`w-fit px-5 py-2 rounded-xl bg-slate-50 border transition-all duration-200 outline-none font-semibold text-slate-700 placeholder:text-slate-400
            ${
              errors.aadharNumber
                ? "border-rose-300 focus:border-rose-500 focus:ring-4 focus:ring-rose-500/10"
                : "border-slate-600 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
            }`}
              />
              {errors.aadharNumber && (
                <p className="text-rose-500 text-xs font-bold mt-1 ml-1">
                  {errors.aadharNumber}
                </p>
              )}
            </div>

            {/* Action Button */}
            <div className="w-full md:w-auto">
              <button
                type="button"
                onClick={handleSubmit}
                className="w-full md:w-40 h-[44px] flex items-center justify-center gap-2 text-white bg-blue-600 hover:bg-blue-700 active:scale-95 font-bold rounded-2xl shadow-lg shadow-blue-600/20 transition-all duration-200"
              >
                <span className="tracking-wide">Verify</span>
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.5"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Bottom Security Badge */}
          <div className="mt-8 pt-6 border-t border-slate-100 flex items-center justify-center gap-6">
            <div className="flex items-center gap-2 grayscale opacity-50">
              <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-tighter">
                Encrypted Connection
              </span>
            </div>
            <div className="flex items-center gap-2 grayscale opacity-50">
              <div className="w-2 h-2 rounded-full bg-blue-500"></div>
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-tighter">
                Official API
              </span>
            </div>
          </div>
        </div>
      </div>

      {listData &&
        typeof listData === "object" &&
        listData.masked_pan &&
        listData.client_id && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 px-4 py-8 bg-slate-50 min-h-fit">
            {/* Left Column: Raw Data Visualizer */}
            <div className="bg-white border border-slate-200 rounded-3xl shadow-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                <h2 className="text-sm font-bold text-slate-500 uppercase tracking-widest">
                  Source JSON
                </h2>
              </div>
              <pre className="bg-slate-900 p-5 rounded-2xl text-[13px] text-blue-300 overflow-x-auto font-mono leading-relaxed shadow-inner">
                {JSON.stringify(listData, null, 2)}
              </pre>
            </div>

            {/* Right Column: Beautiful Status Card */}
            <div className="flex flex-col justify-center">
              <div className="bg-white border border-slate-200 rounded-3xl shadow-xl shadow-slate-200/50 overflow-hidden">
                {/* Header Section with Status Color */}
                <div
                  className={`p-6 ${listData?.linking_status ? "bg-emerald-50" : "bg-rose-50"} border-b border-slate-100`}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-2xl font-extrabold text-slate-800">
                        Verification Result
                      </h3>
                      <p className="text-slate-500 text-sm mt-1">
                        Aadhaar-PAN Linkage Information
                      </p>
                    </div>
                    <div
                      className={`flex items-center gap-2 px-4 py-2 rounded-full font-bold text-sm ${
                        listData?.linking_status
                          ? "bg-emerald-500 text-white"
                          : "bg-rose-500 text-white"
                      }`}
                    >
                      {listData?.linking_status ? "● Linked" : "● Not Linked"}
                    </div>
                  </div>
                </div>

                {/* Body Section */}
                <div className="p-8 space-y-6">
                  <div className="grid grid-cols-2 gap-6">
                    {/* PAN Card Section */}
                    <div className="space-y-1">
                      <label className="text-[11px] font-black text-slate-400 uppercase tracking-wider">
                        PAN Number
                      </label>
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-mono font-bold text-slate-700">
                          {listData?.masked_pan}
                        </span>
                        <span className="bg-blue-100 text-blue-600 text-[10px] px-2 py-0.5 rounded font-bold uppercase">
                          Masked
                        </span>
                      </div>
                    </div>

                    {/* Client ID Section */}
                    <div className="space-y-1">
                      <label className="text-[11px] font-black text-slate-400 uppercase tracking-wider">
                        Client Reference
                      </label>
                      <p
                        className="text-sm font-medium text-slate-600 truncate"
                        title={listData?.client_id}
                      >
                        {listData?.client_id.split("-")[0]}...
                      </p>
                    </div>
                  </div>

                  {/* Reason Banner */}
                  <div className="bg-slate-50 border border-slate-100 rounded-2xl p-4 flex items-start gap-4">
                    <div className="bg-white p-2 rounded-lg shadow-sm border border-slate-100">
                      <svg
                        className="w-5 h-5 text-slate-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-tight">
                        System Remarks
                      </p>
                      <p className="text-slate-700 font-medium capitalize">
                        {listData?.reason || "No remarks provided"}
                      </p>
                      {listData?.detailed_reason && (
                        <p className="text-slate-500 text-xs mt-1">
                          {listData?.detailed_reason}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Footer Action */}
                <div className="px-8 py-4 bg-slate-50/50 border-t border-slate-100 flex justify-end">
                  <button className="text-sm font-bold text-blue-600 hover:text-blue-700 transition-colors">
                    Download Report →
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
    </>
  );
};

export default AadharPankLink;
